<template>
    <div 
        :class="[$style.titleBar, { [$style.expanded]: !isNavBarOpen }]" 
        v-if="showTitleBar" 
        data-qa-id="bulk-action-title-bar"
    >
        <!-- Left section: Close icon and selected count -->
        <div :class="$style.details">
            <MdIconClose 
                data-qa-id="close-icon" 
                :class="$style.icon" 
                color="white" 
                @click="closeTitleBar"
            />
            <MdTypography 
                v-if="componentName === 'ingestion-accordion'" 
                data-qa-id="selected-bulk-edit-count-data" 
                tag="h1" 
                color="white" 
                variant="body1--medium"
            >
                {{ $t('pages.inventory_ingestion_hub.titleBar.selected_families_count', {
                    selectedCount: selectedCount, 
                    familyText: selectedCount > 1 ? "families" : "family"
                }) }}
            </MdTypography>
            <MdTypography 
                v-else 
                data-qa-id="seleceted-count-data" 
                tag="h1" 
                color="white" 
                variant="body1--medium"
            >
                {{ $t('pages.inventory_ingestion_hub.titleBar.selected_cnt', {
                    selectedCount: selectedCount
                }) }}
            </MdTypography>
        </div>

        <!-- Right section: Actions -->
        <div :class="$style.actions">
            <!-- Main Actions -->
            <template v-for="(action, index) in mainActions" :key="`action-${index}`">
                <div>
                    <div 
                        :class="[$style.actionItem, $style.icon]" 
                        @click="handleActionClick(action)"
                    >
                        <!-- Dynamic Icon Component -->
                        <component 
                            :is="getIconComponent(action)" 
                            :data-qa-id="`${getDataQaId(action)}-icon`"
                            color="white"
                            :rotate="action.iconRotate"
                        />
                        <MdTypography 
                            :data-qa-id="`${getDataQaId(action)}-text`"
                            tag="h1" 
                            color="white" 
                            variant="body2--medium"
                        >
                            {{ action.text }}
                        </MdTypography>
                    </div>
                    <!-- Slot for custom content (e.g., modals) -->
                    <slot v-if="action.slotName" :name="action.slotName" />
                </div>
            </template>

            <!-- More Dropdown Menu -->
            <div 
                v-if="showMoreMenu" 
                :class="[$style.actionItem, $style.icon, $style.moreDropdownContainer]"
            >
                <div :class="$style.divider"></div>
                <div :class="$style.moreButton" @click="toggleMoreDropdown">
                    <MdIconKebabMenu 
                        data-qa-id="more-icon" 
                        color="white" 
                        rotate="right" 
                    />
                    <MdTypography 
                        data-qa-id="more-text" 
                        tag="h1" 
                        color="white" 
                        variant="body2--medium"
                    >
                        More
                    </MdTypography>
                </div>
                <div 
                    v-if="isMoreDropdownOpen"
                    :class="$style.moreDropdown"
                    @click.stop
                >
                    <div 
                        v-for="(menuAction, menuIndex) in moreMenuActions" 
                        :key="`more-action-${menuIndex}`"
                        :class="$style.dropdownItem" 
                        @click="handleMoreOptionClick(menuAction)"
                    >
                        <component 
                            :is="getIconComponent(menuAction)" 
                            :data-qa-id="`${getDataQaId(menuAction)}-dropdown-icon`"
                            color="spirits--600" 
                        />
                        <MdTypography 
                            :data-qa-id="`${getDataQaId(menuAction)}-dropdown-text`"
                            tag="span" 
                            color="spirits--900" 
                            variant="body2--medium"
                        >
                            {{ menuAction.text }}
                        </MdTypography>
                    </div>
                </div>
            </div>

            <!-- Delete Action (if configured) -->
            <div v-if="deleteConfiguration.visible" :class="$style.deleteActionItem">
                <MdIconEmptyTrash 
                    data-qa-id="delete-icon" 
                    :class="$style.icon" 
                    color="white" 
                    aria-label="open modal button" 
                    @click="openDeleteModal"
                />
                <MdModal 
                    v-if="showDeleteModal"
                    data-qa-id="delete-modal" 
                    :class="$style.deleteModal" 
                    id="delete-modal" 
                    arialabelledby="modal-title" 
                    ariadescribedby="modal-description" 
                    showoverlay="true" 
                    bordered="false" 
                    width="730px" 
                    :isopen="showDeleteModal" 
                    showclosebutton="true" 
                    disablefocustrap="true" 
                    closeonescapekey="false" 
                    enablebackdropclick="false" 
                    isfullpage="false" 
                    transition="none" 
                    @handleclose="closeDeleteModal"
                >
                    <div slot="header" data-qa-id="delete-modal-header">
                        <MdTypography 
                            data-qa-id="delete-modal-header-info" 
                            tag="h5" 
                            textalign="start" 
                            color="spirits--900" 
                            variant="heading5--medium" 
                            id="modal-title"
                        >
                            {{ deleteConfiguration.modalText?.header ? deleteConfiguration.modalText.header : 
                                $t(`pages.inventory_ingestion_hub.titleBar.${usedForFamily ? 'family_' : ''}delete_header`, {
                                selectedCount: selectedCount
                                }) 
                            }}
                        </MdTypography>
                    </div>
                    <div slot="body" data-qa-id="delete-modal-body" :class="$style.deleteBody">
                        <MdTypography 
                            data-qa-id="delete-modal-body-info" 
                            tag="p" 
                            textalign="start" 
                            color="spirits--700" 
                            variant="body1--regular" 
                            id="modal-description"
                        >
                            {{ deleteConfiguration.modalText?.body ? deleteConfiguration.modalText.body : 
                                $t(`pages.inventory_ingestion_hub.titleBar.${usedForFamily ? 'family_' : ''}delete_body`, {
                                selectedCount: selectedCount
                                }) 
                            }}
                        </MdTypography>
                    </div>
                    <div slot="footer" :class="$style.deleteFooter" data-qa-id="delete-modal-footer">
                        <MdButton 
                            data-qa-id="delete-modal-footer-cancelBtn" 
                            size="large" 
                            color="lightgrey" 
                            @click="closeDeleteModal"
                        >
                            {{ $t('pages.inventory_ingestion_hub.titleBar.cancel_btn') }}
                        </MdButton>
                        <MdButton 
                            data-qa-id="delete-modal-footer-deleteBtn" 
                            size="large" 
                            color="red" 
                            @click="deleteConfiguration.action" 
                            :disabled="deleteConfiguration.loading"
                        >
                            {{ $t('pages.inventory_ingestion_hub.titleBar.delete_btn') }}
                            <MdSpinner 
                                v-if="deleteConfiguration.loading" 
                                slot="icon-start" 
                                aria-label="Deleting" 
                                size="16" 
                                showbox="false"
                                color="white"
                            />
                        </MdButton>                      
                    </div>
                </MdModal>
            </div>
        </div>
    </div>
</template>
